import { useRef, useState } from "react";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { useActiveShow } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { emptyTotals, planImport, readTabletFile, runImport } from "../util/tabletSheetsFile";

// #3575 -- Review Queue "Import sheets": staff pick the sheets file a scribe
// tablet saved (Reset tablet writes it to a USB stick, #3568) and it goes to
// the cloud through POST /tablet-file/import (#3571) in chunks. Nothing is
// written here: the server re-sends every item through the normal scribe write,
// and a sheet that clashes becomes a held copy in this very Review Queue.
//
// States of `outcome`:
//   { kind: "done", ... }        everything decided; shows N imported, M held
//   { kind: "stopped", ... }     the server (or the network) stopped part way;
//                                Retry resends what is left, in order
//   { kind: "refused", message } the file itself was refused (reason + fix)
//   { kind: "badFile", reason }  caught in the browser before sending
//
// i18n keys are static (lookup tables of literal keys, never built from data).
const BAD_FILE_KEY = {
  invalid: "reviewQueue.importSheets.badFile.invalid",
  unknownKind: "reviewQueue.importSheets.badFile.unknownKind",
  unknownVersion: "reviewQueue.importSheets.badFile.unknownVersion",
};

// #3577 -- why a stopped import stopped, by the server's HTTP status for the item
// it stopped on (score-api tablet-file-import.js isTransientStatus: 401, 408, 429
// and 5xx; a stop with no status is the network or a call that decided nothing).
// Literal keys only.
const STOP_REASON = {
  signedOut: { why: "reviewQueue.importSheets.stoppedReason.signedOut", fix: "reviewQueue.importSheets.stoppedFix.signedOut" },
  timedOut: { why: "reviewQueue.importSheets.stoppedReason.timedOut", fix: "reviewQueue.importSheets.stoppedFix.timedOut" },
  busy: { why: "reviewQueue.importSheets.stoppedReason.busy", fix: "reviewQueue.importSheets.stoppedFix.busy" },
  serverError: { why: "reviewQueue.importSheets.stoppedReason.serverError", fix: "reviewQueue.importSheets.stoppedFix.serverError" },
  noAnswer: { why: "reviewQueue.importSheets.stoppedReason.noAnswer", fix: "reviewQueue.importSheets.stoppedFix.noAnswer" },
  other: { why: "reviewQueue.importSheets.stoppedReason.other", fix: "reviewQueue.importSheets.stoppedFix.other" },
};

function stopReasonOf(status) {
  if (status === 401) return "signedOut";
  if (status === 408) return "timedOut";
  if (status === 429) return "busy";
  if (typeof status === "number" && status >= 500) return "serverError";
  if (status == null) return "noAnswer";
  return "other";
}

// FileReader rather than Blob.text(): works in every browser and in jsdom.
function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function refusedMessage(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if ((status === 400 || status === 403) && typeof body?.code === "string" && body.code.startsWith("tablet_file_")) {
    return body?.data?.message ?? null;
  }
  return undefined;
}

export default function ImportSheetsButton({ activeShowId, onImported, onGoToTodo }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const auth = useAuth();
  const { shows } = useActiveShow();
  const inputRef = useRef(null);
  // The file being imported: what Retry needs to carry on without re-reading it.
  const jobRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const [signedInAgain, setSignedInAgain] = useState(false);

  const send = async (body) => unwrap(await scoreApi.tabletImport.importTabletSheetsFile(body));

  const run = async (job) => {
    setBusy(true);
    setOutcome(null);
    setSignedInAgain(false);
    setProgress({ left: job.pending.length, total: job.total });
    let result;
    try {
      result = await runImport({
        send,
        file: job.file,
        items: job.pending,
        queuedRideKeys: job.queuedRideKeys,
        activeShowId,
        totals: job.totals,
        state: job.state,
      });
    } finally {
      setBusy(false);
      setProgress(null);
    }
    jobRef.current = {
      ...job,
      pending: result.pending,
      totals: result.totals,
      state: {
        showIds: result.showIds,
        fileShow: result.fileShow,
        station: result.station,
        differentShow: result.differentShow,
      },
    };
    const summary = {
      totals: result.totals,
      left: result.pending.length,
      fileShow: result.fileShow,
      station: result.station,
      differentShow: result.differentShow,
      showIds: result.showIds,
    };
    const refused = result.error ? refusedMessage(result.error) : undefined;
    if (result.error && refused !== undefined) {
      jobRef.current = null;
      setOutcome({ kind: "refused", message: refused, ...summary });
    } else if (result.error || result.stopped) {
      // The server's stop, or the failed call itself (a 401 from the route, a dropped connection).
      const status = result.stopped?.status ?? result.error?.response?.status ?? null;
      const code = result.stopped?.code ?? result.error?.response?.data?.code ?? null;
      setOutcome({ kind: "stopped", code, status, ...summary });
    } else {
      jobRef.current = null;
      setOutcome({ kind: "done", ...summary });
    }
    // Anything imported or held changes the queue: reload it.
    if (result.totals.imported + result.totals.held > 0) await onImported?.();
  };

  const onPick = async (event) => {
    const picked = event.target.files?.[0];
    // Let the same file be picked again after a refusal.
    event.target.value = "";
    if (!picked) return;
    setOutcome(null);
    let text;
    try {
      text = await readAsText(picked);
    } catch {
      setOutcome({ kind: "badFile", reason: "invalid" });
      return;
    }
    const read = readTabletFile(text);
    if (!read.ok) {
      setOutcome({ kind: "badFile", reason: read.reason });
      return;
    }
    const plan = planImport(read.file);
    // The server needs the file header on every chunk; the bulk lives in the items.
    const BULK = new Set(["manualRides", "sheets", "localDrafts", "items"]);
    const header = Object.fromEntries(Object.entries(read.file).filter(([key]) => !BULK.has(key)));
    const job = {
      file: header,
      pending: plan.items,
      total: plan.items.length,
      queuedRideKeys: plan.queuedRideKeys,
      totals: emptyTotals(),
      state: {},
    };
    jobRef.current = job;
    await run(job);
  };

  const retry = async () => {
    if (jobRef.current && !busy) await run(jobRef.current);
  };

  // The same recovery CogntioAuthProvider uses: renew quietly (same page, so the
  // file and what is left to send are kept), and only if that fails go through the
  // full sign-in page.
  const signInAgain = async () => {
    setSigningIn(true);
    try {
      const renewed = await auth.signinSilent();
      if (renewed) setSignedInAgain(true);
      else await auth.signinRedirect();
    } catch {
      await auth.signinRedirect();
    } finally {
      setSigningIn(false);
    }
  };

  const chooseAnother = () => inputRef.current?.click();

  const stopKey = outcome?.kind === "stopped" ? stopReasonOf(outcome.status) : null;
  // #3577 -- the shows the rides LANDED in (showIds) that are not the open one.
  // fileShow is only where the tablet was working, and may well be the open show.
  const otherShowIds = outcome?.kind === "done" ? (outcome.showIds ?? []).filter((id) => id && id !== activeShowId) : [];
  const showName = (id) =>
    shows?.find((s) => s.id === id)?.name || (outcome?.fileShow?.id === id ? outcome.fileShow.name : "") || id;
  const otherShowNames = otherShowIds.map(showName).join(", ");

  return (
    <Box sx={{ mb: 2 }} data-testid="import-sheets">
      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={onPick}
        data-testid="import-sheets-input"
      />
      <Button variant="outlined" onClick={chooseAnother} disabled={busy} data-testid="import-sheets-button">
        {t("reviewQueue.importSheets.button")}
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t("reviewQueue.importSheets.hint")}
      </Typography>

      {busy && progress && (
        <Alert severity="info" sx={{ mt: 1 }} data-testid="import-sheets-progress">
          {t("reviewQueue.importSheets.progress", { left: progress.left, total: progress.total })}
        </Alert>
      )}

      {outcome?.kind === "badFile" && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          data-testid="import-sheets-error"
          action={
            <Button color="inherit" size="small" onClick={chooseAnother} data-testid="import-sheets-choose-another">
              {t("reviewQueue.importSheets.chooseAnother")}
            </Button>
          }
        >
          {t(BAD_FILE_KEY[outcome.reason] ?? BAD_FILE_KEY.invalid)}
        </Alert>
      )}

      {outcome?.kind === "refused" && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          data-testid="import-sheets-error"
          action={
            <Button color="inherit" size="small" onClick={chooseAnother} data-testid="import-sheets-choose-another">
              {t("reviewQueue.importSheets.chooseAnother")}
            </Button>
          }
        >
          {outcome.message || t("reviewQueue.importSheets.refusedNoReason")}
        </Alert>
      )}

      {outcome?.kind === "stopped" && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          data-testid="import-sheets-stopped"
          action={
            <>
              {stopKey === "signedOut" && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={signInAgain}
                  disabled={busy || signingIn}
                  data-testid="import-sheets-sign-in"
                >
                  {t("reviewQueue.importSheets.signIn")}
                </Button>
              )}
              <Button color="inherit" size="small" onClick={retry} disabled={busy} data-testid="import-sheets-retry">
                {t("reviewQueue.importSheets.retry")}
              </Button>
            </>
          }
        >
          <div data-testid="import-sheets-stopped-text">
            {t("reviewQueue.importSheets.stopped", {
              imported: outcome.totals.imported,
              held: outcome.totals.held,
              left: outcome.left,
            })}
          </div>
          <div data-testid="import-sheets-stopped-why">{t(STOP_REASON[stopKey].why)}</div>
          <div data-testid="import-sheets-stopped-fix">
            {signedInAgain ? t("reviewQueue.importSheets.signedInAgain") : t(STOP_REASON[stopKey].fix)}
          </div>
          {outcome.code && (
            <Typography variant="caption" component="div" data-testid="import-sheets-stopped-detail">
              {t("reviewQueue.importSheets.stoppedDetail", { code: outcome.code })}
            </Typography>
          )}
        </Alert>
      )}

      {outcome?.kind === "done" && (
        <Alert
          severity={outcome.totals.held > 0 ? "warning" : "success"}
          sx={{ mt: 1 }}
          data-testid="import-sheets-result"
          action={
            onGoToTodo ? (
              <Button color="inherit" size="small" onClick={onGoToTodo} data-testid="import-sheets-go-todo">
                {t("reviewQueue.importSheets.goToTodo")}
              </Button>
            ) : undefined
          }
        >
          <div data-testid="import-sheets-result-text">
            {t("reviewQueue.importSheets.result", {
              count: outcome.totals.imported,
              imported: outcome.totals.imported,
              held: outcome.totals.held,
            })}
          </div>
          {outcome.totals.alreadyImported > 0 && (
            <div data-testid="import-sheets-already">
              {t("reviewQueue.importSheets.alreadyImported", { count: outcome.totals.alreadyImported })}
            </div>
          )}
          {outcome.totals.inProgress > 0 && (
            <div data-testid="import-sheets-in-progress">
              {t("reviewQueue.importSheets.inProgress", { count: outcome.totals.inProgress })}
            </div>
          )}
          {outcome.differentShow && otherShowIds.length > 0 && (
            <div data-testid="import-sheets-different-show">
              {t("reviewQueue.importSheets.differentShow", { shows: otherShowNames })}
            </div>
          )}
          {outcome.totals.held > 0 && (
            <div>
              <Link
                component={RouterLink}
                to="/review-queue"
                data-testid="import-sheets-review-link"
                sx={{ verticalAlign: "baseline" }}
              >
                {t("reviewQueue.importSheets.reviewLink")}
              </Link>
            </div>
          )}
        </Alert>
      )}
    </Box>
  );
}
